import clsx from 'clsx';
import { Zap, AlertTriangle, CheckCircle } from 'lucide-react';

export interface BullpenData {
  teamId: number;
  teamAbbr: string;
  era: number;
  whip: number;
  inningsPitchedLast3: number;
  pitchersUsedLast3: number;
  fatigueLevel: 'rested' | 'moderate' | 'taxed';
  saves?: number;
  blownSaves?: number;
}

interface Props {
  awayBullpen?: BullpenData;
  homeBullpen?: BullpenData;
}

const FATIGUE_CONFIG = {
  rested:   { label: 'Rested',   color: 'text-win',  bg: 'bg-win/10',  icon: CheckCircle },
  moderate: { label: 'Moderate', color: 'text-warn', bg: 'bg-warn/10', icon: Zap },
  taxed:    { label: 'Taxed',    color: 'text-loss', bg: 'bg-loss/10', icon: AlertTriangle },
} as const;

function BullpenPanel({ data }: { data?: BullpenData }) {
  if (!data) {
    return (
      <div className="card p-4 flex items-center justify-center text-gray-600 text-sm">
        Bullpen data unavailable
      </div>
    );
  }

  const fatigue = FATIGUE_CONFIG[data.fatigueLevel];
  const FatigueIcon = fatigue.icon;

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-100">{data.teamAbbr} Bullpen</span>
        <span className={clsx('flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded', fatigue.color, fatigue.bg)}>
          <FatigueIcon className="w-3 h-3" />
          {fatigue.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-surface-4">
        <div className="text-center">
          <div className={clsx('text-sm font-mono font-semibold', data.era < 3.80 ? 'text-win' : data.era < 4.80 ? 'text-warn' : 'text-loss')}>
            {data.era.toFixed(2)}
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">3-Day ERA</div>
        </div>
        <div className="text-center">
          <div className={clsx('text-sm font-mono font-semibold', data.whip < 1.20 ? 'text-win' : data.whip < 1.45 ? 'text-warn' : 'text-loss')}>
            {data.whip.toFixed(2)}
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">3-Day WHIP</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-surface-4 text-center">
        <div>
          <div className="text-sm font-mono text-gray-200">{data.inningsPitchedLast3.toFixed(1)}</div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">IP Last 3d</div>
        </div>
        <div>
          <div className="text-sm font-mono text-gray-200">{data.pitchersUsedLast3}</div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">Arms Used</div>
        </div>
      </div>

      {(data.saves != null || data.blownSaves != null) && (
        <div className="flex items-center gap-4 pt-2 border-t border-surface-4 text-xs text-gray-500">
          {data.saves != null && <span>{data.saves} SV</span>}
          {data.blownSaves != null && <span className={data.blownSaves > 5 ? 'text-warn' : ''}>{data.blownSaves} BS</span>}
        </div>
      )}
    </div>
  );
}

export function BullpenAnalytics({ awayBullpen, homeBullpen }: Props) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Bullpen Availability</div>
      <div className="grid grid-cols-2 gap-4">
        <BullpenPanel data={awayBullpen} />
        <BullpenPanel data={homeBullpen} />
      </div>
    </div>
  );
}
